:root {
  --tile-back: rgb(140, 92, 50);
}

body {
  max-width: 30rem;
  padding: 0 1rem;
  margin: auto;
}

.grid {
  --tile-size: 2.5rem;
  display: grid;
  grid-template-columns: repeat(var(--size), var(--tile-size));
  gap: calc(var(--tile-size) * 0.1);
}

.cell {
  composes: shading from "../../game/tile.module.css";
  all: unset;
  display: grid;
  outline: revert;
  outline-offset: 2.5px;
  border-radius: var(--tile-radius);
  cursor: pointer;
  touch-action: manipulation;

  &:active {
    scale: 0.94;
  }

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.1);
    }
  }
}

.faceUp {
  composes: tile faceUp from "../../game/tile.module.css";
  rotate: var(--tilt-after-flip);
}

.flipping {
  composes: tile from "../../game/tile.module.css";
  --half-thickness: calc(var(--tile-size) * 0.095);
  --tile-perspective: calc(var(--tile-size) * 4.6);
  --bevel-cut: calc(var(--tile-size) * 0.045);
  --toss: 0.26s;
  --toss-height: 0.76;
  position: relative;
  transform-style: preserve-3d;
  scale: calc(1 - var(--half-thickness) / var(--tile-perspective));
  animation: flip var(--toss) linear both;

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: var(--tile-radius);
    box-shadow: var(--tile-bevel);
    backface-visibility: hidden;
  }

  &::before {
    background-color: var(--from);
    transform: translateZ(var(--half-thickness));
  }

  &::after {
    background-color: var(--to);
    transform: translateZ(calc(var(--half-thickness) * -1))
      rotate3d(var(--flip-axis), 180deg);
  }
}

.toBack {
  --from: var(--tile-base-color);
  --to: var(--tile-back);
}

.toFront {
  --from: var(--tile-back);
  --to: var(--tile-base-color);
}

.slice {
  position: absolute;
  inset: calc((1 - var(--out)) * var(--bevel-cut));
  border-radius: calc(var(--tile-radius) - (1 - var(--out)) * var(--bevel-cut));
  transform: translateZ(calc(var(--z) * var(--half-thickness)));
  background-color: color-mix(in srgb, var(--from) var(--mix), var(--to));
  box-shadow: var(--tile-bevel);
  /* Looks like dead code. It is not: a tile keeps .flipping after it lands,
     and Chrome leaves paint around slices left live in the compositor. */
  animation: tile-body var(--toss) step-end both;
}

.hashes {
  composes: shading from "../../game/tile.module.css";
  --tile-size: 1.1em;
  display: flex;
  flex-direction: column;
  font-family: monospace;
  letter-spacing: 0.2ch;
  line-height: 1rem;
  padding-left: 0;

  li {
    list-style: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    > span {
      position: relative;
      flex: none;
      width: var(--tile-size);
      height: var(--tile-size);
      border-radius: var(--tile-radius);
      background-color: var(--tile-base-color);
      box-shadow: var(--tile-bevel);

      &::after {
        content: "";
        position: absolute;
        inset: calc(var(--tile-size) * var(--inlay) / 6);
        border-radius: calc(var(--tile-radius) / 2);
        background-color: var(--tile-back);
        box-shadow: inset 0 calc(var(--tile-shade) * 2)
          calc(var(--tile-shade) * 3) rgb(0 0 0 / 0.45);
      }

      &.small {
        --inlay: 2;
      }

      &.medium {
        --inlay: 1;
      }

      &.large {
        background-color: var(--tile-back);

        &::after {
          content: none;
        }
      }
    }
  }
}

@keyframes flip {
  from {
    transform: perspective(var(--tile-perspective)) translateZ(0)
      rotateZ(var(--tilt)) rotate3d(var(--flip-axis), 0deg);
    z-index: 0;
  }
  25% {
    transform: perspective(var(--tile-perspective))
      translateZ(calc(var(--tile-size) * var(--toss-height) * 0.75))
      rotateZ(calc(var(--tilt) * 0.75 + var(--tilt-after-flip) * 0.25))
      rotate3d(var(--flip-axis), 45deg);
    z-index: 10;
  }
  50% {
    transform: perspective(var(--tile-perspective))
      translateZ(calc(var(--tile-size) * var(--toss-height)))
      rotateZ(calc(var(--tilt) * 0.5 + var(--tilt-after-flip) * 0.5))
      rotate3d(var(--flip-axis), 90deg);
  }
  75% {
    transform: perspective(var(--tile-perspective))
      translateZ(calc(var(--tile-size) * var(--toss-height) * 0.75))
      rotateZ(calc(var(--tilt) * 0.25 + var(--tilt-after-flip) * 0.75))
      rotate3d(var(--flip-axis), 135deg);
    z-index: 10;
  }
  to {
    transform: perspective(var(--tile-perspective)) translateZ(0)
      rotateZ(var(--tilt-after-flip)) rotate3d(var(--flip-axis), 180deg);
    z-index: 0;
  }
}

@keyframes tile-body {
  from {
    visibility: hidden;
  }
  4%,
  96% {
    visibility: visible;
  }
  97%,
  to {
    visibility: hidden;
  }
}
